import { useCallback } from 'react';
import type { Component, Page, Site, PageLayout } from '../../schemas/validation';
import type { PreviewData, PreviewState } from './types';
import { createPreviewApiClient } from './apiClient';
import { subscriptionsApi } from '../../api/client';
import { resolveSiteThemeForPreview } from '../../utils/resolveSiteThemeForPreview';

interface UseLoadPreviewDataProps {
  siteId: string | undefined;
  pageId: string | undefined;
  state: PreviewState;
}

export const useLoadPreviewData = ({ siteId, pageId, state }: UseLoadPreviewDataProps) => {
  const loadPreviewData = useCallback(async () => {
    try {
      state.setIsLoading(true);
      state.setError(null);

      const apiClient = createPreviewApiClient();

      console.log('🔍 Preview Debug: Loading preview data for:', { siteId, pageId });
      console.log('🔍 Preview Debug: API Base URL:', apiClient.defaults.baseURL);
      console.log(
        '🔍 Preview Debug: Auth token:',
        localStorage.getItem('authToken') ? 'Present' : 'Missing'
      );

      let themeColors: any = null;
      let apiUrl: string;
      let useOldApproach = false;

      if (pageId) {
        apiUrl = `/sites/${siteId}/preview/page/${pageId}`;
      } else {
        apiUrl = `/sites/${siteId}/preview`;
      }

      console.log('🔍 Preview Debug: Trying new API endpoint:', apiUrl);

      try {
        const response = await apiClient.get<{ success: boolean; data: PreviewData }>(apiUrl);
        console.log('🔍 Preview Debug: New API response received:', response.data);

        const responseData = response.data.success ? response.data.data : (response.data as any);

        const components = parseComponents(responseData);
        logHeadContentComponents(components);

        themeColors = await resolveSiteThemeForPreview({
          serverTheme: responseData.theme,
          site: responseData.site,
          fetchThemeByName: themeName => loadThemeColors(apiClient, themeName),
        });
        if (responseData.theme) {
          console.log('🎨 PagePreview: Resolved theme (server/brand/preset):', responseData.theme);
        }

        const layout = parseLayout(responseData.layout);
        console.log(
          '📐 PagePreview: Layout data:',
          layout ? { name: layout.name, componentsCount: layout.components?.length } : 'No layout'
        );

        // Check subscription to determine if branding should show
        const showBranding = await checkShowBranding();

        state.setPreviewData({
          site: responseData.site,
          page: responseData.page,
          components: components,
          navigation: responseData.navigation || [],
          theme: themeColors || null,
          layout: layout || null,
          showBranding,
        });
      } catch (newApiError: any) {
        console.warn(
          '🔍 Preview Debug: New API failed, falling back to old approach:',
          newApiError.response?.status
        );

        if (newApiError.response?.status === 404) {
          useOldApproach = true;
        } else {
          throw newApiError;
        }
      }

      if (useOldApproach && pageId) {
        console.log('🔍 Preview Debug: Using old API approach for page preview');
        const previewData = await loadWithOldApproach(apiClient, siteId!, pageId);
        // Check subscription to determine if branding should show
        const showBranding = await checkShowBranding();
        state.setPreviewData({ ...previewData, showBranding });
      }
    } catch (err: any) {
      handleError(err, state);
    } finally {
      state.setIsLoading(false);
    }
  }, [siteId, pageId, state]);

  return { loadPreviewData };
};

function parseComponents(responseData: any): Component[] {
  let components: Component[] = [];
  if (responseData.page?.components) {
    try {
      const parsedComponents =
        typeof responseData.page.components === 'string'
          ? JSON.parse(responseData.page.components)
          : responseData.page.components;
      components = parsedComponents.components || parsedComponents || [];
    } catch (error) {
      console.error('Error parsing page components:', error);
    }
  }
  console.log('🔍 PagePreview: Setting preview data with components:', components);
  return components;
}

function logHeadContentComponents(components: Component[]) {
  const headComponents = components.filter((c: any) => c.type === 'head-content');
  if (headComponents.length > 0) {
    console.log(
      '🔍 PagePreview: Head-content components data:',
      headComponents.map((c: any) => {
        const data = c.data as any;
        return {
          id: c.id,
          type: c.type,
          hasData: !!c.data,
          dataKeys: c.data ? Object.keys(c.data) : [],
          title: data?.title,
          description: data?.description,
          keywords: data?.keywords,
        };
      })
    );
    headComponents.forEach((c: any) => {
      console.log('📋 FULL SEO DATA (new API):', JSON.stringify(c.data, null, 2));
    });
  } else {
    console.warn('⚠️ No head-content component found in page components (new API)!');
  }
}

async function loadThemeColors(
  apiClient: ReturnType<typeof createPreviewApiClient>,
  themeName: string
): Promise<any> {
  try {
    const themeResponse = await apiClient.get(`/themes/${themeName}`);
    if (themeResponse.data) {
      console.log(
        '🎨 PagePreview: Loaded theme colors for',
        themeName,
        themeResponse.data.customColors
      );
      return themeResponse.data.customColors;
    }
  } catch (themeError) {
    console.warn('Failed to load theme colors, will use defaults:', themeError);
  }
  return null;
}

function parseLayout(layout: any): PageLayout | null {
  if (!layout) return null;
  if (layout.components && typeof layout.components === 'string') {
    try {
      const parsedLayoutComponents = JSON.parse(layout.components);
      return {
        ...layout,
        components: parsedLayoutComponents.components || parsedLayoutComponents || [],
      };
    } catch (e) {
      console.error('Error parsing layout components:', e);
    }
  }
  return layout;
}

async function loadLayoutForPage(
  apiClient: ReturnType<typeof createPreviewApiClient>,
  siteId: string,
  pageData: Page
): Promise<PageLayout | null> {
  if (pageData.layoutId) {
    const layoutResponse = await apiClient.get(`/layouts/${pageData.layoutId}`);
    return layoutResponse.data || null;
  }

  const defaultResponse = await apiClient.get(`/layouts/site/${siteId}/default`);
  if (defaultResponse.data?.layout) {
    return defaultResponse.data.layout;
  }

  const listResponse = await apiClient.get<{ layouts: PageLayout[] }>(`/layouts/site/${siteId}`);
  const layouts = listResponse.data?.layouts || [];
  return layouts.find(layout => layout.isDefault) || layouts[0] || null;
}

async function loadWithOldApproach(
  apiClient: ReturnType<typeof createPreviewApiClient>,
  siteId: string,
  pageId: string
): Promise<PreviewData> {
  const [pageResponse, componentsResponse] = await Promise.all([
    apiClient.get<Page>(`/pages/${pageId}`),
    apiClient.get<{ components: Component[] }>(`/pages/${pageId}/components`),
  ]);

  console.log('🔍 Preview Debug: Old API responses received');

  const pageData = pageResponse.data;
  const componentsData = componentsResponse.data;

  const seoComponent = componentsData.components?.find((c: any) => c.type === 'head-content');
  if (seoComponent) {
    const seoData = seoComponent.data as any;
    console.log('🔍 SEO Component from API:', {
      id: seoComponent.id,
      type: seoComponent.type,
      hasData: !!seoComponent.data,
      dataKeys: Object.keys(seoComponent.data || {}),
      title: seoData?.title,
      description: seoData?.description,
      keywords: seoData?.keywords,
      canonicalUrl: seoData?.canonicalUrl,
      favicon: seoData?.favicon,
    });
    console.log('📋 FULL SEO DATA FROM API:', JSON.stringify(seoComponent.data, null, 2));
  } else {
    console.warn('⚠️ No head-content component found in page components!');
  }

  let siteData: Site;
  try {
    const siteResponse = await apiClient.get<Site>(`/sites/${siteId}`);
    siteData = siteResponse.data;
  } catch {
    console.warn('🔍 Preview Debug: Failed to get site from API, trying localStorage');
    const sitesData = localStorage.getItem('sites');
    if (sitesData) {
      const sites = JSON.parse(sitesData);
      siteData = sites.find((s: Site) => s.id === siteId);
      if (!siteData) {
        throw new Error('Site not found');
      }
    } else {
      throw new Error('Site not found');
    }
  }

  const sortedComponents = componentsData.components
    .filter((component: Component) => {
      if (!component.visible) return false;
      if (!component.id || !component.type || !component.data) {
        console.warn('Invalid component found, skipping:', component);
        return false;
      }
      return true;
    })
    .sort((a: Component, b: Component) => a.order - b.order);

  console.log('🔍 PagePreview: Setting preview data with theme:', siteData.theme);

  const themeColors = await resolveSiteThemeForPreview({
    site: siteData,
    fetchThemeByName: themeName => loadThemeColors(apiClient, themeName),
  });

  let pageLayout: PageLayout | null = null;
  if (pageData.showLayout !== false) {
    try {
      pageLayout = await loadLayoutForPage(apiClient, siteId, pageData);

      if (pageLayout && pageLayout.components && typeof pageLayout.components === 'string') {
        const parsedComponents = JSON.parse(pageLayout.components);
        pageLayout = {
          ...pageLayout,
          components: parsedComponents.components || parsedComponents || [],
        };
      }

      if (pageLayout) {
        console.log('📐 PagePreview (old API): Loaded layout:', pageLayout.name);
      }
    } catch (layoutError) {
      console.warn('Failed to load page layout:', layoutError);
    }
  }

  return {
    site: siteData,
    page: pageData,
    components: sortedComponents,
    navigation: [],
    theme: themeColors || 'standard',
    layout: pageLayout,
  };
}

async function checkShowBranding(): Promise<boolean> {
  try {
    const response = await subscriptionsApi.getCurrentSubscription();
    // API returns { subscription: {...} | null, usage: { plan: { name: '...' } } }
    const planName = response.subscription?.planName || response.usage?.plan?.name;
    // Show branding if no plan or on FREE plan
    const showBranding = !planName || planName.toUpperCase() === 'FREE';
    console.log(
      '🔍 PagePreview: Subscription check - planName:',
      planName,
      'showBranding:',
      showBranding
    );
    return showBranding;
  } catch (error) {
    console.warn(
      '🔍 PagePreview: Could not check subscription, defaulting to show branding:',
      error
    );
    return true; // Default to show branding on error
  }
}

function handleError(err: any, state: PreviewState) {
  console.error('🔍 Preview Debug: Failed to load preview data:', err);
  console.error('🔍 Preview Debug: Error details:', {
    status: err.response?.status,
    statusText: err.response?.statusText,
    data: err.response?.data,
    config: {
      url: err.config?.url,
      method: err.config?.method,
      baseURL: err.config?.baseURL,
      headers: err.config?.headers,
    },
  });

  if (err.response) {
    const status = err.response.status;
    const data = err.response.data;

    if (status === 401) {
      state.setError('Authentication failed. Please log in again.');
    } else if (status === 404) {
      state.setError('Page not found. It may have been deleted.');
    } else if (status === 500) {
      state.setError(
        'Server error: The page data may be corrupted. Please try editing the page and saving it again, or contact support if the issue persists.'
      );
    } else {
      const message = data?.message || err.message || 'Failed to load page preview';
      state.setError(`Request failed (${status}): ${message}`);
    }
  } else {
    state.setError('Network error: Failed to connect to the server');
  }
}
